import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

// ── Brand tokens (from your color spec) ──────────────────────────────────────
const T = {
  dark: "#2E4053", // header / primary dark
  darkDeep: "#1C2A3D", // hover state
  accent: "#2E4053", // buttons, focus rings
  accentHov: "#1C2A3D",
  muted: "#34495E", // muted text
  border: "#A6AAAD", // borders
  surface: "#F4F6F9", // page surface
  surfaceAlt: "#D1D8E0", // selected bg tint
  chipText: "#D1D8E0", // chip text on dark header
  white: "#FFFFFF",
  green: "#16a34a",
  greenLight: "#f0fdf4",
};

// ── Data ──────────────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: "📊",
    title: "Accounting & Finance",
    items: [
      "General Ledger",
      "Journal Entries",
      "AR / AP",
      "Bank Reconciliation",
      "Financial Statements",
      "Tax Management",
      "Budgeting",
    ],
  },
  {
    icon: "🛒",
    title: "Sales Management",
    items: [
      "Quotations",
      "Sales Orders",
      "Invoicing",
      "Customer Management",
      "Sales Tracking",
      "Payment Collection",
    ],
  },
  {
    icon: "📦",
    title: "Inventory Management",
    items: [
      "Real-Time Stock",
      "Stock Transfers",
      "Warehouse Management",
      "Barcode Support",
      "Batch Tracking",
      "Stock Valuation",
      "Low Stock Alerts",
    ],
  },
  {
    icon: "🤝",
    title: "CRM",
    items: [
      "Lead Management",
      "Opportunities",
      "Customer Database",
      "Activities & Follow-Ups",
      "Sales Pipeline",
    ],
  },
  {
    icon: "👥",
    title: "Human Resources",
    items: [
      "Employee Records",
      "Attendance",
      "Leave Management",
      "Payroll",
      "Performance Tracking",
    ],
  },
  {
    icon: "🏭",
    title: "Purchase Management",
    items: [
      "Purchase Requests",
      "Purchase Orders",
      "Supplier Management",
      "Goods Receipt",
      "Vendor Payments",
    ],
  },
  {
    icon: "📣",
    title: "Marketing",
    items: [
      "Campaign Management",
      "Customer Segmentation",
      "Promotions",
      "Analytics",
    ],
  },
  {
    icon: "📈",
    title: "Reports & Analytics",
    items: [
      "Executive Dashboards",
      "Profit & Loss",
      "Balance Sheet",
      "Cash Flow",
      "Inventory Reports",
      "Custom Reports",
    ],
  },
];

const INDUSTRIES = [
  {
    icon: "🛍️",
    name: "Retail",
    desc: "Manage stores, inventory, sales, and customers.",
  },
  {
    icon: "🚚",
    name: "Wholesale & Distribution",
    desc: "Track products across warehouses and locations.",
  },
  {
    icon: "🏗️",
    name: "Manufacturing",
    desc: "Control production, inventory, purchasing, and costing.",
  },
  {
    icon: "🏥",
    name: "Healthcare",
    desc: "Manage clinics, appointments, billing, and patient records.",
  },
  {
    icon: "💄",
    name: "Cosmetics & Beauty",
    desc: "Track products, distributors, inventory, and sales channels.",
  },
  {
    icon: "🛒",
    name: "E-Commerce",
    desc: "Connect online stores with inventory and accounting.",
  },
  {
    icon: "💼",
    name: "Services",
    desc: "Manage projects, clients, invoices, and finances.",
  },
];

const WHY = [
  {
    icon: "⚡",
    title: "Simplicity First",
    desc: "No complicated interfaces. Your team starts using Spade ERP with minimal training.",
  },
  {
    icon: "💰",
    title: "Affordable Pricing",
    desc: "Enterprise-level capabilities at a fraction of the cost of traditional ERP systems.",
  },
  {
    icon: "🔗",
    title: "All-In-One Platform",
    desc: "No disconnected systems. Everything works together seamlessly in one place.",
  },
  {
    icon: "📐",
    title: "Scalable Architecture",
    desc: "Start small and grow without ever changing systems or migrating data.",
  },
  {
    icon: "📊",
    title: "Real-Time Insights",
    desc: "Make data-driven decisions with live dashboards and reports built for clarity.",
  },
  {
    icon: "🏢",
    title: "Multi-Branch Management",
    desc: "Manage multiple stores, warehouses, and locations from one platform.",
  },
  {
    icon: "🔒",
    title: "Secure & Reliable",
    desc: "Enterprise-grade security and role-based permissions on every action.",
  },
  {
    icon: "🎯",
    title: "Dedicated Support",
    desc: "Fast onboarding, implementation, and ongoing Arabic-language support.",
  },
];

const COMPARE = [
  { feature: "Easy to Use", spade: true, sap: false, oracle: false, odoo: "△" },
  { feature: "Affordable", spade: true, sap: false, oracle: false, odoo: true },
  {
    feature: "Fast Deployment",
    spade: true,
    sap: false,
    oracle: false,
    odoo: "△",
  },
  { feature: "Multi-Branch", spade: true, sap: true, oracle: true, odoo: true },
  { feature: "Accounting", spade: true, sap: true, oracle: true, odoo: true },
  { feature: "CRM", spade: true, sap: true, oracle: true, odoo: true },
  { feature: "Inventory", spade: true, sap: true, oracle: true, odoo: true },
  { feature: "Customizable", spade: true, sap: true, oracle: true, odoo: true },
  {
    feature: "Dedicated Support",
    spade: true,
    sap: "△",
    oracle: "△",
    odoo: "△",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Spade ERP transformed our operations. We reduced manual work by 70% and gained full visibility across all branches.",
    name: "Mohamed A.",
    role: "Operations Director",
    industry: "Wholesale",
  },
  {
    quote:
      "Easy to learn and powerful. Our team became productive within days, not months like other ERPs we tried.",
    name: "Sara K.",
    role: "Finance Manager",
    industry: "Retail",
  },
  {
    quote:
      "Affordable and scalable. Everything we needed without the high cost of enterprise software.",
    name: "Ahmed T.",
    role: "CEO",
    industry: "Manufacturing",
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const useInView = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const Reveal = ({ children, delay = 0 }) => {
  const [ref, vis] = useInView();
  return (
    <div
      ref={ref}
      style={{
        opacity: vis ? 1 : 0,
        transform: vis ? "translateY(0)" : "translateY(28px)",
        transition: `opacity .6s ease ${delay}s, transform .6s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

const CheckIcon = ({ color = T.green }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <circle cx="9" cy="9" r="9" fill={color} fillOpacity=".12" />
    <path
      d="M5 9l3 3 5-5"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SectionLabel = ({ children }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 3,
      color: T.dark,
      textTransform: "uppercase",
      marginBottom: 16,
    }}
  >
    <span
      style={{
        width: 20,
        height: 2,
        background: T.dark,
        display: "inline-block",
        borderRadius: 2,
      }}
    />
    {children}
    <span
      style={{
        width: 20,
        height: 2,
        background: T.dark,
        display: "inline-block",
        borderRadius: 2,
      }}
    />
  </div>
);

// ── Nav ───────────────────────────────────────────────────────────────────────
const Nav = ({ onCTA }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: scrolled ? "rgba(46,64,83,.97)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        transition: "background .3s, box-shadow .3s",
        boxShadow: scrolled ? "0 1px 0 rgba(255,255,255,.06)" : "none",
        padding: "0 32px",
        height: 68,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 32,
            height: 32,
            background: T.chipText,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 900, color: T.dark }}>
            S
          </span>
        </div>
        <span
          style={{
            fontSize: 18,
            fontWeight: 800,
            color: T.white,
            letterSpacing: -0.5,
          }}
        >
          Spade ERP
        </span>
      </div>
      <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
        {["Features", "Industries", "Pricing"].map((l) => (
          <a
            key={l}
            href={`#${l.toLowerCase()}`}
            style={{
              color: T.chipText,
              fontSize: 14,
              fontWeight: 500,
              textDecoration: "none",
              opacity: 0.85,
              transition: "opacity .15s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = 1)}
            onMouseLeave={(e) => (e.target.style.opacity = 0.85)}
          >
            {l}
          </a>
        ))}
        <button
          onClick={onCTA}
          style={{
            background: T.white,
            color: T.dark,
            border: "none",
            borderRadius: 8,
            padding: "9px 20px",
            fontSize: 14,
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          Start Free →
        </button>
      </div>
    </nav>
  );
};

// ── Hero ──────────────────────────────────────────────────────────────────────
const Hero = ({ onCTA, onDemo }) => {
  const [count, setCount] = useState({ users: 0, companies: 0, modules: 0 });
  useEffect(() => {
    const targets = { users: 5000, companies: 300, modules: 40 };
    const duration = 1800;
    const start = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setCount({
        users: Math.round(targets.users * ease),
        companies: Math.round(targets.companies * ease),
        modules: Math.round(targets.modules * ease),
      });
      if (p < 1) requestAnimationFrame(tick);
    };
    const t = setTimeout(() => requestAnimationFrame(tick), 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        background: `linear-gradient(135deg, ${T.dark} 0%, #1a2b3c 40%, #0d1f30 100%)`,
        position: "relative",
        overflow: "hidden",
        padding: "100px 32px 80px",
      }}
    >
      {/* Geometric background decoration */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -180,
            right: -180,
            width: 560,
            height: 560,
            borderRadius: "50%",
            border: `1px solid rgba(209,216,224,.08)`,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 360,
            height: 360,
            borderRadius: "50%",
            border: `1px solid rgba(209,216,224,.12)`,
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -200,
            left: -200,
            width: 600,
            height: 600,
            borderRadius: "50%",
            border: `1px solid rgba(209,216,224,.06)`,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "30%",
            right: "8%",
            width: 120,
            height: 120,
            background: "rgba(209,216,224,.04)",
            borderRadius: 20,
            transform: "rotate(15deg)",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "15%",
            right: "20%",
            width: 60,
            height: 60,
            background: "rgba(209,216,224,.06)",
            borderRadius: 10,
            transform: "rotate(-10deg)",
          }}
        />
      </div>

      <div
        style={{
          maxWidth: 820,
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
          textAlign: "center",
        }}
      >
        {/* Eyebrow chip */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "rgba(209,216,224,.1)",
            border: "1px solid rgba(209,216,224,.2)",
            borderRadius: 30,
            padding: "6px 16px",
            marginBottom: 32,
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: "#4ade80",
              display: "inline-block",
              boxShadow: "0 0 8px #4ade80",
            }}
          />
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: T.chipText,
              letterSpacing: 1,
            }}
          >
            NOW AVAILABLE
          </span>
        </div>

        <h1
          style={{
            fontSize: "clamp(36px,5.5vw,64px)",
            fontWeight: 900,
            color: T.white,
            lineHeight: 1.08,
            letterSpacing: -2,
            margin: "0 0 24px",
          }}
        >
          Run Your Entire Business
          <br />
          <span
            style={{
              color: T.chipText,
              fontWeight: 400,
              fontStyle: "italic",
              letterSpacing: -1,
            }}
          >
            From One Platform.
          </span>
        </h1>

        <p
          style={{
            fontSize: "clamp(16px,2vw,19px)",
            color: "rgba(209,216,224,.8)",
            lineHeight: 1.72,
            maxWidth: 580,
            margin: "0 auto 40px",
          }}
        >
          Spade ERP helps businesses manage sales, inventory, accounting, CRM,
          purchasing, warehouses, branches, and reports — without
          enterprise-level complexity or cost.
        </p>

        <div
          style={{
            display: "flex",
            gap: 14,
            justifyContent: "center",
            flexWrap: "wrap",
            marginBottom: 56,
          }}
        >
          <button
            onClick={onCTA}
            style={{
              background: T.white,
              color: T.dark,
              border: "none",
              borderRadius: 10,
              padding: "15px 32px",
              fontSize: 16,
              fontWeight: 800,
              cursor: "pointer",
              boxShadow: "0 4px 24px rgba(0,0,0,.25)",
              transition: "transform .15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "scale(1.03)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Start Free Trial →
          </button>
          <button
            onClick={onDemo}
            style={{
              background: "transparent",
              color: T.white,
              border: "1.5px solid rgba(209,216,224,.35)",
              borderRadius: 10,
              padding: "15px 32px",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
              transition: "border-color .15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.borderColor = "rgba(209,216,224,.7)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.borderColor = "rgba(209,216,224,.35)")
            }
          >
            Book a Demo
          </button>
        </div>

        {/* Stats */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 0,
            flexWrap: "wrap",
            borderTop: "1px solid rgba(209,216,224,.12)",
            paddingTop: 40,
          }}
        >
          {[
            { val: `${count.users.toLocaleString()}+`, label: "Active Users" },
            { val: `${count.companies}+`, label: "Companies" },
            { val: `${count.modules}+`, label: "ERP Modules" },
            { val: "99.9%", label: "Uptime SLA" },
          ].map((s, i) => (
            <div
              key={s.label}
              style={{
                flex: "1 1 120px",
                padding: "0 24px",
                borderRight: i < 3 ? "1px solid rgba(209,216,224,.12)" : "none",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "clamp(26px,3vw,38px)",
                  fontWeight: 900,
                  color: T.white,
                  letterSpacing: -1,
                  lineHeight: 1,
                }}
              >
                {s.val}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "rgba(209,216,224,.6)",
                  marginTop: 6,
                  fontWeight: 500,
                }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Feature chips */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 10,
          flexWrap: "wrap",
          marginTop: 48,
          position: "relative",
          zIndex: 1,
        }}
      >
        {[
          "Multi-Branch Support",
          "Real-Time Reporting",
          "Cloud & On-Premise",
          "Fast Setup",
          "Affordable Pricing",
          // "ETA e-invoicing",
        ].map((chip) => (
          <span
            key={chip}
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: T.chipText,
              background: "rgba(209,216,224,.1)",
              border: "1px solid rgba(209,216,224,.15)",
              borderRadius: 20,
              padding: "5px 14px",
            }}
          >
            {chip}
          </span>
        ))}
      </div>
    </section>
  );
};

// ── About ─────────────────────────────────────────────────────────────────────
const About = () => {
  const [ref, vis] = useInView();
  return (
    <section style={{ background: T.white, padding: "96px 32px" }}>
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 80,
          alignItems: "center",
        }}
      >
        <Reveal>
          <SectionLabel>About Spade ERP</SectionLabel>
          <h2
            style={{
              fontSize: "clamp(28px,3vw,42px)",
              fontWeight: 800,
              color: T.dark,
              lineHeight: 1.15,
              letterSpacing: -1,
              margin: "0 0 20px",
            }}
          >
            Enterprise Power.
            <br />
            <span style={{ color: T.muted, fontWeight: 400 }}>
              Small Business Simplicity.
            </span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: T.muted,
              lineHeight: 1.8,
              marginBottom: 24,
            }}
          >
            Spade ERP is an all-in-one business management platform designed to
            simplify operations and help companies scale faster. Whether you're
            managing inventory, accounting, customer relationships, warehouses,
            or multiple branches — it's all here.
          </p>
          <p style={{ fontSize: 16, color: T.muted, lineHeight: 1.8 }}>
            Unlike traditional ERP solutions that require months of
            implementation and expensive consultants, Spade ERP is designed to
            be intuitive, affordable, and ready to use from day one.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            {[
              {
                icon: "🎯",
                title: "Our Mission",
                desc: "Empower businesses of all sizes with enterprise-grade technology that is simple, affordable, and accessible.",
              },
              {
                icon: "🌍",
                title: "Our Vision",
                desc: "Become the leading ERP platform for SMBs across the Middle East, Africa, Europe, and beyond.",
              },
              {
                icon: "⚡",
                title: "Fast Setup",
                desc: "Go live in days, not months. No expensive consultants or lengthy implementations required.",
              },
              {
                icon: "💬",
                title: "Arabic Support",
                desc: "Dedicated Arabic-language support team available to help you succeed.",
              },
            ].map((c) => (
              <div
                key={c.title}
                style={{
                  background: T.surface,
                  borderRadius: 14,
                  padding: "22px 20px",
                  border: `1px solid ${T.surfaceAlt}`,
                }}
              >
                <div style={{ fontSize: 26, marginBottom: 10 }}>{c.icon}</div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: T.dark,
                    marginBottom: 6,
                  }}
                >
                  {c.title}
                </div>
                <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.65 }}>
                  {c.desc}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ── Why Choose ────────────────────────────────────────────────────────────────
const WhyChoose = () => (
  <section
    style={{ background: T.surface, padding: "96px 32px" }}
    id="features"
  >
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <Reveal>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <SectionLabel>Why Choose Spade</SectionLabel>
          <h2
            style={{
              fontSize: "clamp(28px,3vw,40px)",
              fontWeight: 800,
              color: T.dark,
              letterSpacing: -1,
              margin: "0 0 14px",
            }}
          >
            Built differently. Priced fairly.
          </h2>
          <p
            style={{
              fontSize: 16,
              color: T.muted,
              maxWidth: 520,
              margin: "0 auto",
              lineHeight: 1.7,
            }}
          >
            Eight reasons why 300+ companies chose Spade ERP over SAP, Oracle,
            and Odoo.
          </p>
        </div>
      </Reveal>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
          gap: 20,
        }}
      >
        {WHY.map((w, i) => (
          <Reveal key={w.title} delay={i * 0.05}>
            <div
              style={{
                background: T.white,
                borderRadius: 14,
                padding: "26px 22px",
                border: `1px solid ${T.surfaceAlt}`,
                height: "100%",
                boxSizing: "border-box",
                transition: "box-shadow .2s, transform .2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 8px 28px rgba(46,64,83,.1)";
                e.currentTarget.style.transform = "translateY(-3px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "none";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div style={{ fontSize: 28, marginBottom: 12 }}>{w.icon}</div>
              <h3
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: T.dark,
                  margin: "0 0 8px",
                }}
              >
                {w.title}
              </h3>
              <p
                style={{
                  fontSize: 13.5,
                  color: T.muted,
                  lineHeight: 1.65,
                  margin: 0,
                }}
              >
                {w.desc}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ── Features ──────────────────────────────────────────────────────────────────
const Features = () => {
  const [active, setActive] = useState(0);
  return (
    <section style={{ background: T.white, padding: "96px 32px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <SectionLabel>Core Features</SectionLabel>
            <h2
              style={{
                fontSize: "clamp(28px,3vw,40px)",
                fontWeight: 800,
                color: T.dark,
                letterSpacing: -1,
                margin: "0 0 14px",
              }}
            >
              Everything your business needs.
            </h2>
            <p
              style={{
                fontSize: 16,
                color: T.muted,
                maxWidth: 480,
                margin: "0 auto",
                lineHeight: 1.7,
              }}
            >
              40+ modules covering every aspect of your operations, all
              connected in one system.
            </p>
          </div>
        </Reveal>

        <div
          style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 32 }}
        >
          {/* Tab list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {FEATURES.map((f, i) => (
              <button
                key={f.title}
                onClick={() => setActive(i)}
                style={{
                  background: active === i ? T.surfaceAlt : "transparent",
                  border: `1px solid ${
                    active === i ? T.border : "transparent"
                  }`,
                  borderRadius: 10,
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background .15s",
                }}
              >
                <span style={{ fontSize: 20 }}>{f.icon}</span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: active === i ? 700 : 500,
                    color: active === i ? T.dark : T.muted,
                  }}
                >
                  {f.title}
                </span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div
            style={{
              background: T.surface,
              borderRadius: 16,
              padding: "36px 32px",
              border: `1px solid ${T.surfaceAlt}`,
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 12 }}>
              {FEATURES[active].icon}
            </div>
            <h3
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: T.dark,
                margin: "0 0 24px",
                letterSpacing: -0.5,
              }}
            >
              {FEATURES[active].title}
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "10px 24px",
              }}
            >
              {FEATURES[active].items.map((item) => (
                <div
                  key={item}
                  style={{ display: "flex", alignItems: "center", gap: 10 }}
                >
                  <CheckIcon />
                  <span
                    style={{ fontSize: 14, color: T.muted, fontWeight: 500 }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Industries ────────────────────────────────────────────────────────────────
const Industries = () => (
  <section style={{ background: T.dark, padding: "96px 32px" }} id="industries">
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <Reveal>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <SectionLabel>
            <span style={{ color: T.chipText }}>Industries We Serve</span>
          </SectionLabel>
          <h2
            style={{
              fontSize: "clamp(28px,3vw,40px)",
              fontWeight: 800,
              color: T.white,
              letterSpacing: -1,
              margin: "0 0 14px",
            }}
          >
            Built for your industry.
          </h2>
          <p
            style={{
              fontSize: 16,
              color: T.chipText,
              opacity: 0.75,
              maxWidth: 480,
              margin: "0 auto",
              lineHeight: 1.7,
            }}
          >
            Spade ERP adapts to how your specific industry works — not the other
            way around.
          </p>
        </div>
      </Reveal>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))",
          gap: 16,
        }}
      >
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.name} delay={i * 0.06}>
            <div
              style={{
                background: "rgba(255,255,255,.05)",
                border: "1px solid rgba(209,216,224,.1)",
                borderRadius: 14,
                padding: "28px 22px",
                textAlign: "center",
                transition: "background .2s, transform .2s",
                cursor: "default",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,.09)";
                e.currentTarget.style.transform = "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,.05)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              <div style={{ fontSize: 36, marginBottom: 14 }}>{ind.icon}</div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: T.white,
                  marginBottom: 8,
                }}
              >
                {ind.name}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: T.chipText,
                  opacity: 0.7,
                  lineHeight: 1.6,
                }}
              >
                {ind.desc}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ── Comparison ────────────────────────────────────────────────────────────────
const Comparison = () => {
  const Val = ({ v }) => {
    if (v === true)
      return (
        <span style={{ color: T.green, fontWeight: 800, fontSize: 18 }}>✓</span>
      );
    if (v === false)
      return <span style={{ color: T.border, fontSize: 16 }}>✗</span>;
    return <span style={{ color: T.muted, fontSize: 15 }}>△</span>;
  };
  return (
    <section style={{ background: T.surface, padding: "96px 32px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <Reveal>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <SectionLabel>How We Compare</SectionLabel>
            <h2
              style={{
                fontSize: "clamp(28px,3vw,40px)",
                fontWeight: 800,
                color: T.dark,
                letterSpacing: -1,
                margin: "0 0 14px",
              }}
            >
              Spade vs. the enterprise giants.
            </h2>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div
            style={{
              overflowX: "auto",
              borderRadius: 16,
              border: `1px solid ${T.surfaceAlt}`,
              boxShadow: "0 2px 12px rgba(0,0,0,.06)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      background: T.dark,
                      color: T.white,
                      fontSize: 13,
                      fontWeight: 700,
                      borderRadius: "14px 0 0 0",
                    }}
                  >
                    Feature
                  </th>
                  {[
                    ["Spade ERP", T.dark],
                    ["SAP", "#374151"],
                    ["Oracle", "#374151"],
                    ["Odoo", "#374151"],
                  ].map(([name, bg], i) => (
                    <th
                      key={name}
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        background: i === 0 ? "#1C2A3D" : "#374151",
                        color: i === 0 ? T.white : "#9ca3af",
                        fontSize: 13,
                        fontWeight: 700,
                        borderRadius: i === 3 ? "0 14px 0 0" : 0,
                      }}
                    >
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row, i) => (
                  <tr
                    key={row.feature}
                    style={{ background: i % 2 === 0 ? T.white : T.surface }}
                  >
                    <td
                      style={{
                        padding: "14px 20px",
                        fontWeight: 500,
                        color: T.dark,
                        borderBottom: `1px solid ${T.surfaceAlt}`,
                      }}
                    >
                      {row.feature}
                    </td>
                    {[row.spade, row.sap, row.oracle, row.odoo].map((v, j) => (
                      <td
                        key={j}
                        style={{
                          padding: "14px 20px",
                          textAlign: "center",
                          borderBottom: `1px solid ${T.surfaceAlt}`,
                          background:
                            j === 0 ? "rgba(46,64,83,.04)" : "transparent",
                        }}
                      >
                        <Val v={v} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

// ── Testimonials ──────────────────────────────────────────────────────────────
const Testimonials = () => (
  <section style={{ background: T.white, padding: "96px 32px" }}>
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <Reveal>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <SectionLabel>What Our Customers Say</SectionLabel>
          <h2
            style={{
              fontSize: "clamp(28px,3vw,40px)",
              fontWeight: 800,
              color: T.dark,
              letterSpacing: -1,
              margin: 0,
            }}
          >
            Trusted by growing businesses.
          </h2>
        </div>
      </Reveal>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
          gap: 24,
        }}
      >
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={i * 0.1}>
            <div
              style={{
                background: T.surface,
                borderRadius: 16,
                padding: "32px 28px",
                border: `1px solid ${T.surfaceAlt}`,
                position: "relative",
              }}
            >
              <div
                style={{
                  fontSize: 48,
                  color: T.surfaceAlt,
                  fontFamily: "Georgia,serif",
                  lineHeight: 1,
                  marginBottom: 12,
                  userSelect: "none",
                }}
              >
                "
              </div>
              <p
                style={{
                  fontSize: 15,
                  color: T.dark,
                  lineHeight: 1.75,
                  margin: "0 0 24px",
                  fontStyle: "italic",
                }}
              >
                {t.quote}
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    background: T.dark,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: T.white,
                    fontWeight: 800,
                    fontSize: 15,
                  }}
                >
                  {t.name[0]}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.dark }}>
                    {t.name}
                  </div>
                  <div style={{ fontSize: 12, color: T.muted }}>
                    {t.role} · {t.industry}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// ── Final CTA ─────────────────────────────────────────────────────────────────
const FinalCTA = ({ onCTA, onDemo }) => (
  <section
    style={{
      background: `linear-gradient(135deg, ${T.dark} 0%, #0d1f30 100%)`,
      padding: "100px 32px",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: -100,
        left: "50%",
        transform: "translateX(-50%)",
        width: 600,
        height: 600,
        borderRadius: "50%",
        background: "rgba(209,216,224,.04)",
        pointerEvents: "none",
      }}
    />
    <Reveal>
      <div style={{ position: "relative", zIndex: 1 }}>
        <SectionLabel>
          <span style={{ color: T.chipText }}>Get Started Today</span>
        </SectionLabel>
        <h2
          style={{
            fontSize: "clamp(32px,4vw,52px)",
            fontWeight: 900,
            color: T.white,
            letterSpacing: -1.5,
            margin: "0 0 20px",
            lineHeight: 1.1,
          }}
        >
          Ready to grow your business?
        </h2>
        <p
          style={{
            fontSize: 18,
            color: T.chipText,
            opacity: 0.75,
            maxWidth: 500,
            margin: "0 auto 44px",
            lineHeight: 1.7,
          }}
        >
          Join 300+ businesses using Spade ERP to streamline operations, cut
          costs, and make smarter decisions.
        </p>
        <div
          style={{
            display: "flex",
            gap: 14,
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={onCTA}
            style={{
              background: T.white,
              color: T.dark,
              border: "none",
              borderRadius: 10,
              padding: "16px 36px",
              fontSize: 16,
              fontWeight: 800,
              cursor: "pointer",
              boxShadow: "0 4px 24px rgba(0,0,0,.3)",
              transition: "transform .15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "scale(1.03)")
            }
            onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
          >
            Start Free Trial →
          </button>
          <button
            onClick={onDemo}
            style={{
              background: "transparent",
              color: T.white,
              border: "1.5px solid rgba(209,216,224,.3)",
              borderRadius: 10,
              padding: "16px 36px",
              fontSize: 16,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Book a Personalized Demo
          </button>
        </div>
        <div
          style={{
            marginTop: 40,
            display: "flex",
            justifyContent: "center",
            gap: "24px 40px",
            flexWrap: "wrap",
          }}
        >
          {[
            "No credit card required",
            "14-day free trial",
            // "ETA e-invoicing included",
            "Cancel anytime",
          ].map((t) => (
            <div
              key={t}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                fontSize: 13,
                color: T.chipText,
                opacity: 0.7,
              }}
            >
              <CheckIcon color="#4ade80" />
              {t}
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  </section>
);

// ── Footer ────────────────────────────────────────────────────────────────────
const Footer = () => (
  <footer
    style={{
      background: "#0a1628",
      padding: "48px 32px 32px",
      borderTop: "1px solid rgba(209,216,224,.06)",
    }}
  >
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 32,
          marginBottom: 40,
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                background: T.chipText,
                borderRadius: 7,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: 14, fontWeight: 900, color: T.dark }}>
                S
              </span>
            </div>
            <span style={{ fontSize: 16, fontWeight: 800, color: T.white }}>
              Spade ERP
            </span>
          </div>
          <p
            style={{
              fontSize: 13,
              color: T.chipText,
              opacity: 0.5,
              maxWidth: 260,
              lineHeight: 1.65,
              margin: 0,
            }}
          >
            Enterprise-grade ERP for businesses that are serious about growth.
          </p>
        </div>
        <div style={{ display: "flex", gap: 48, flexWrap: "wrap" }}>
          {[
            {
              title: "Product",
              links: ["Features", "Pricing", "Industries", "Roadmap"],
            },
            {
              title: "Company",
              links: ["About", "Careers", "Contact", "Blog"],
            },
            {
              title: "Support",
              links: ["Documentation", "Help Center", "Status", "Security"],
            },
          ].map((col) => (
            <div key={col.title}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: T.chipText,
                  opacity: 0.5,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginBottom: 14,
                }}
              >
                {col.title}
              </div>
              {col.links.map((l) => (
                <div
                  key={l}
                  style={{
                    fontSize: 13,
                    color: T.chipText,
                    opacity: 0.6,
                    marginBottom: 10,
                    cursor: "pointer",
                  }}
                >
                  {l}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div
        style={{
          borderTop: "1px solid rgba(209,216,224,.08)",
          paddingTop: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <span style={{ fontSize: 12, color: T.chipText, opacity: 0.4 }}>
          © 2026 Spade ERP. All rights reserved.
        </span>
        {/* <span style={{ fontSize: 12, color: T.chipText, opacity: 0.4 }}>
          ETA Certified · ZATCA Compliant · Arabic Support
        </span> */}
      </div>
    </div>
  </footer>
);

// ── Main LandingPage ──────────────────────────────────────────────────────────
const SpadeHomePage = () => {
  const navigate = useNavigate();
  const onCTA = () => navigate("/register");
  const onDemo = () => navigate("/dashboard/ContactUsResponses");

  return (
    <div
      style={{
        fontFamily:
          "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
        background: T.surface,
      }}
    >
      <Nav onCTA={onCTA} />
      <Hero onCTA={onCTA} onDemo={onDemo} />
      <About />
      <WhyChoose />
      <Features />
      <Industries />
      <Comparison />
      <Testimonials />
      <FinalCTA onCTA={onCTA} onDemo={onDemo} />
      <Footer />
    </div>
  );
};

export default SpadeHomePage;
